import { useEffect, useRef } from 'react';
import type { MouseEvent, PointerEvent } from 'react';
import { APPUI_LONG_MS, CLIC_DU_LACHER_MS, LIBRE, suivreLAppui, type EtatDAppui, type EvenementDAppui } from './appuiLong';

/**
 * Les deux gestes d'une bulle, sur la même cible, partout (SPEC §5.3, retour 3 des testeurs) :
 * **un clic** entre dedans, **un double-clic** ouvre sa fiche. Carte, Liste, Hiérarchie et
 * « Projets » s'en servent ; la bulle du centre a les siens, plus bas.
 *
 * Au doigt, **un appui long** ouvre la fiche à la place du double-clic (ticket 12 du chantier
 * `telephone`, règle dans `appuiLong.ts`) : les deux fonctions de ce fichier le portent, et
 * tous ceux qui s'en servent en profitent sans rien changer chez eux. Un toucher bref entre,
 * comme un clic.
 *
 * Sans précaution, le premier clic part avant que le second n'arrive : on descend dans la
 * bulle, et le second clic tombe sur l'écran d'après. Le clic simple attend donc de voir si
 * un second suit.
 *
 * **Le délai.** Il était de 200 ms : trop court. Au test du tuto (ticket 31), un double-clic
 * d'Igor sur « trouver un artisan » est entré dans la bulle au lieu d'ouvrir sa fiche : son
 * second clic arrivait après 200 ms, bien avant la limite du système (500 ms sous Windows).
 * À 400 ms, un double-clic posé passe, et la descente d'un simple clic reste vive (ticket 33).
 *
 * Le navigateur compte les clics (`detail`) : un second clic reconnu comme tel annule
 * l'entrée même si l'évènement `dblclick` n'arrive pas sur la même bulle.
 */
const ATTENTE_MS = 400;

/*
 * L'appui long, branché sur les évènements pointeur. L'état est gardé par élément, pas par
 * composant : `gestesDuCentre` n'est pas un crochet React, et une même bulle de la carte porte
 * les gestes sur deux éléments (le corps et la flèche). Les évènements ne sont jamais arrêtés :
 * le glisser de la carte (`cameraCarte.ts`, sur le parent) les reçoit comme avant.
 */
interface Suivi {
  etat: EtatDAppui;
  minuteur: ReturnType<typeof setTimeout> | null;
}

const suivis = new WeakMap<Element, Suivi>();
/** Les éléments où un doigt est posé et le minuteur court : ceux qu'un pincement annule. */
const armes = new Set<Element>();

/**
 * Un second doigt s'est posé sur la carte : c'est un pincement (ticket 15 du chantier
 * `telephone`, `cameraCarte.ts`), pas un appui long. Ni fiche ni clic à avaler.
 */
export function annulerLesAppuisLongs() {
  for (const element of [...armes]) suivre(element, { type: 'annule' }, () => {});
}

function suivre(element: Element, evenement: EvenementDAppui, surAppuiLong: () => void) {
  const suivi = suivis.get(element) ?? { etat: LIBRE, minuteur: null };
  suivis.set(element, suivi);
  const suite = suivreLAppui(suivi.etat, evenement);
  suivi.etat = suite.etat;
  if (suite.etat.phase === 'pose') armes.add(element);
  else armes.delete(element);
  if (suite.minuteur && suivi.minuteur) {
    clearTimeout(suivi.minuteur);
    suivi.minuteur = null;
  }
  if (suite.minuteur === 'armer') {
    suivi.minuteur = setTimeout(() => {
      suivi.minuteur = null;
      // La bulle a pu quitter l'écran pendant l'appui : rien à ouvrir.
      if (element.isConnected) suivre(element, { type: 'echeance' }, surAppuiLong);
    }, APPUI_LONG_MS);
  }
  if (suite.fiche) {
    surAppuiLong();
    /*
     * L'appui long a pu faire quitter l'écran (en Liste, il entre dans l'étape : chantier
     * `revue-design`, ticket 03). L'élément n'est plus là pour entendre le doigt se lever, et le
     * clic du lâcher tombait sur ce que la nouvelle page montre sous le doigt (une ligne, dont la
     * fiche s'ouvrait). Le lever s'écoute donc aussi sur la fenêtre : élément parti, clic avalé.
     */
    window.addEventListener(
      'pointerup',
      () => {
        if (!element.isConnected) avalerLeClicDuLacher();
      },
      { capture: true, once: true }
    );
  }
  if (suite.avaler) avalerLeClicDuLacher();
}

/** Le clic du lâcher, où qu'il tombe (la fiche ouverte sous le doigt, souvent) : avalé. */
function avalerLeClicDuLacher() {
  const avaler = (clic: globalThis.MouseEvent) => {
    clic.stopPropagation();
    clic.preventDefault();
  };
  window.addEventListener('click', avaler, { capture: true, once: true });
  // Si aucun clic ne suit, on n'en avale pas un plus tard.
  window.setTimeout(() => window.removeEventListener('click', avaler, { capture: true }), CLIC_DU_LACHER_MS);
}

/** Un doigt est-il posé ou tenu sur cet élément ? Alors le menu du navigateur n'a rien à y faire. */
const doigtDessus = (element: Element) => (suivis.get(element)?.etat.phase ?? 'libre') !== 'libre';

/** Les évènements pointeur de l'appui long, à étaler sur l'élément avec ses gestes. */
function gestesDuDoigt(surAppuiLong: () => void) {
  return {
    // Repère pour global.css : ni texte sélectionné, ni loupe, ni « copier » à l'appui long.
    'data-appui-long': true,
    onPointerDown: (evenement: PointerEvent) => {
      suivre(
        evenement.currentTarget,
        { type: 'pose', pointerType: evenement.pointerType, x: evenement.clientX, y: evenement.clientY },
        surAppuiLong
      );
    },
    onPointerMove: (evenement: PointerEvent) => {
      suivre(evenement.currentTarget, { type: 'bouge', x: evenement.clientX, y: evenement.clientY }, surAppuiLong);
    },
    onPointerUp: (evenement: PointerEvent) => {
      suivre(evenement.currentTarget, { type: 'leve' }, surAppuiLong);
    },
    onPointerCancel: (evenement: PointerEvent) => {
      suivre(evenement.currentTarget, { type: 'annule' }, surAppuiLong);
    },
    onContextMenu: (evenement: MouseEvent) => {
      if (doigtDessus(evenement.currentTarget)) evenement.preventDefault();
    },
  };
}

export function useDeuxGestes(surClic: () => void, surDoubleClic: () => void) {
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (minuteur.current) clearTimeout(minuteur.current);
    },
    []
  );

  const annuler = () => {
    if (minuteur.current) {
      clearTimeout(minuteur.current);
      minuteur.current = null;
    }
  };

  return {
    ...gestesDuDoigt(() => {
      annuler();
      surDoubleClic();
    }),
    onClick: (evenement?: MouseEvent) => {
      annuler();
      // Le second clic d'un double-clic : c'est `onDoubleClick` qui répond.
      if (evenement && evenement.detail >= 2) return;
      minuteur.current = setTimeout(() => {
        minuteur.current = null;
        surClic();
      }, ATTENTE_MS);
    },
    onDoubleClick: () => {
      annuler();
      surDoubleClic();
    },
  };
}

/**
 * Au téléphone, un seul geste (chantier `telephone-cadre`, ticket 04, `ligneAuPouce.ts`) : un
 * toucher fait tout de suite ce que la ligne promet (ouvrir la fiche d'une tâche, entrer dans
 * une étape), sans attendre un second clic qui ne viendra pas au doigt ; le doigt gardé posé
 * ouvre la fiche, et le clic du lâcher est avalé. Le titre d'une feuille s'en sert aussi : le
 * toucher comme l'appui long ouvrent sa fiche.
 */
export function gestesDuToucher(surToucher: () => void, surAppuiLong: () => void) {
  return {
    ...gestesDuDoigt(surAppuiLong),
    onClick: surToucher,
  };
}

/**
 * La bulle du centre : on est déjà dedans, un clic n'a nulle part où entrer. Il ne fait donc
 * rien, et **seul le double-clic ouvre sa fiche**, comme sur toute autre bulle (retour 3 des
 * testeurs, qui défait le ticket 20 de finition-v1 : là, un clic l'ouvrait). Le titre de la
 * feuille, dans la tête, a les mêmes gestes : c'est l'autre chemin, et le seul en Liste
 * (« Sa fiche » est parti au ticket 13 de retours-testeurs).
 *
 * Au clavier, Entrée ou Espace sur le bouton arrive comme un clic sans compte de clics
 * (`detail` à 0) : c'est le seul geste du clavier, il ouvre la fiche.
 *
 * Un double-clic plus lent que `ATTENTE_MS` mais dans la limite du système (500 ms sous
 * Windows) : le premier clic est entré dans la bulle, le second tombe sur elle, devenue le
 * centre, et le navigateur y envoie le `dblclick`. Sa fiche s'ouvre donc, une fois entré :
 * c'est bien la fiche demandée, et le tuto la reconnaît par tous les chemins (ticket 43).
 *
 * Au doigt, pas de double-clic : un appui long sur la bulle du centre ou sur le titre ouvre
 * la fiche (ticket 12 du chantier `telephone`). Un toucher bref ne fait rien, comme un clic.
 */
export function gestesDuCentre(surFiche: () => void) {
  return {
    ...gestesDuDoigt(surFiche),
    onClick: (evenement: MouseEvent) => {
      if (evenement.detail === 0) surFiche();
    },
    onDoubleClick: surFiche,
  };
}
